import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, Select, Input, Row, Col, Alert, Space, Typography, Spin, Form, Modal } from "antd";
import { toast } from "react-toastify";

import { postPrescription } from "../api/prescription";
import CreatePrescriptionRequest from "../types/CreatePrescriptionRequest";
import EntityInfo from "../types/EntityInfo";
import { fetchProducts } from "../api/product";
import { fetchPlaces } from "../api/places";
import { fetchMediators } from "../api/mediator";
import Place from "../types/Place";
import Mediator from "../types/Mediator";
import { convertPhoneNumber, selectFilterOption } from "../util/common";
import PhoneInputV2 from "./PhoneInputV2";
import { useTexts } from "../hooks/useTexts";

const { TextArea } = Input;
const { Title } = Typography;

const initialData = {
    placeId: undefined as number | undefined,
    productId: undefined as number | undefined,
    contactPhone: '',
    additionalPhoneNumber: '',
    note: '',
    mediatorId: undefined as number | undefined
};

const CreatePrescription = (): JSX.Element => {
    const { t } = useTexts();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(initialData);
    const [errors, setErrors] = useState<string[]>([]);
    const [products, setProducts] = useState<EntityInfo[]>([]);
    const [places, setPlaces] = useState<Place[]>([]);
    const [mediators, setMediators] = useState<Mediator[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalData, setModalData] = useState<{ message: string, prescriptionId: number | null }>({ message: '', prescriptionId: null });

    const getProducts = () => {
        fetchProducts().then(response => {
            setProducts(response?.items || []);
        }).catch(error => console.error("ERROR", error));
    };

    const getPlaces = () => {
        fetchPlaces().then(response => {
            setPlaces(response?.content || []);
        }).catch(error => console.error("ERROR", error));
    };

    const getMediators = (placeId: number) => {
        fetchMediators({ placeId, pageSize: 100 }).then(response => {
            setMediators(response?.mediators || []);
        }).catch(error => console.error("ERROR", error));
    };

    const handlePhoneNumber = (number: string) => {
        if (!number) return '';
        return convertPhoneNumber(number)?.toString();
    };

    const handleSubmit = (): void => {
        setErrors([]);

        if (!data.productId || !data.placeId) return;

        setLoading(true);
        const request: CreatePrescriptionRequest = {
            productId: data.productId,
            placeId: data.placeId,
            phoneNumber: handlePhoneNumber(data.contactPhone),
            additionalPhoneNumber: handlePhoneNumber(data.additionalPhoneNumber),
            note: data.note,
            mediatorId: data.mediatorId as number
        };
        postPrescription(request)
            .then(response => {
                if (response?.id) {
                    toast.success(t('prescription', 'prescriptionCreated'), { theme: "colored" });
                    setData({
                        ...data,
                        contactPhone: initialData.contactPhone,
                        additionalPhoneNumber: initialData.additionalPhoneNumber,
                        note: initialData.note
                    });
                } else if (!!response?.messages?.length && response?.messages?.length > 0) {
                    setErrors(response.messages);
                }
            })
            .catch(error => {
                if (error?.response?.data?.errorCode === 'error.phoneNumberInUse') {
                    setModalData({
                        message: error?.response?.data?.message,
                        prescriptionId: error?.response?.data?.data?.prescriptionId
                    });
                    setIsModalVisible(true);
                }
                console.error("ERROR", error)
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        getProducts();
        getPlaces();
    }, []);

    useEffect(() => {
        if (data.placeId) {
            getMediators(data.placeId);
        } else {
            setMediators([]);
            setData(prev => ({ ...prev, mediatorId: undefined }));
        }
    }, [data.placeId]);

    return (
        <div style={{ marginTop: '48px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
            <Modal
                title={t('common', 'error') || 'Xəta'}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsModalVisible(false)}>
                        {t('common', 'close') || 'Bağla'}
                    </Button>
                ]}
            >
                <p>{modalData.message}</p>
                {modalData.prescriptionId && (
                    <a href={`/prescriptions/${modalData.prescriptionId}`} target="_blank" rel="noreferrer">
                        {t('prescription', 'viewPrescription') || 'Göndərişə bax'}
                    </a>
                )}
            </Modal>
            {errors?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                    {errors.map((err) => (
                        <Alert key={err} message={err} type="error" showIcon style={{ marginBottom: '8px' }} />
                    ))}
                </div>
            )}

            <div style={{ background: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <Form layout="vertical" className="responsive-form">
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item label={t('place', 'place')} required>
                                <Select
                                    placeholder={t('common', 'none')}
                                    value={data.placeId}
                                    onChange={value => setData({ ...data, placeId: value })}
                                    options={places.map(item => ({ label: item.name, value: item.id }))}
                                    allowClear
                                    showSearch
                                    filterOption={selectFilterOption}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item label={t('product', 'product')} required>
                                <Select
                                    placeholder={t('common', 'none')}
                                    value={data.productId}
                                    onChange={value => setData({ ...data, productId: value })}
                                    options={products.map(item => ({ label: item.name, value: item.id }))}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24}>
                            <Form.Item label={t('mediator', 'mediator')}>
                                <Select
                                    placeholder={t('common', 'none')}
                                    value={data.mediatorId}
                                    onChange={value => setData({ ...data, mediatorId: value })}
                                    options={mediators.map(item => ({
                                        label: `${item.firstname} ${item.lastname}`,
                                        value: item.id
                                    }))}
                                    disabled={!mediators.length}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item label={t('prescription', 'contactPhone')} required>
                                <PhoneInputV2
                                    placeholder={t('prescription', 'contactPhone')}
                                    value={data.contactPhone}
                                    onChange={(value) => setData({ ...data, contactPhone: value })}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item label={t('prescription', 'additionalPhone')}>
                                <PhoneInputV2
                                    placeholder={t('prescription', 'additionalPhone')}
                                    value={data.additionalPhoneNumber}
                                    onChange={(value) => setData({ ...data, additionalPhoneNumber: value })}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24}>
                            <Form.Item>
                                <TextArea
                                    value={data.note}
                                    onChange={(e) => setData({ ...data, note: e.target.value })}
                                    placeholder={t('prescription', 'notePlaceholder')}
                                    autoSize={{ minRows: 4 }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24}>
                            <Button
                                type="primary"
                                onClick={handleSubmit}
                                loading={loading}
                                disabled={
                                    loading ||
                                    !data.placeId ||
                                    !data.productId ||
                                    !(data?.contactPhone?.length >= 9)
                                }
                                block
                            >
                                {t('prescription', 'createPrescription')}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </div>
        </div>
    );
};

export default CreatePrescription;